/**
 * NotificationsDropdown — desktop popover for the notification center (WI-56).
 *
 * Positioned absolute right-0 top-full mt-2 relative to the bell button's
 * containing block. Width: 320px (w-80). Max-height: 440px with overflow-y-auto.
 *
 * Accessibility:
 *   - role="dialog" aria-label="Notifications" aria-modal="true"
 *   - aria-busy="true" while loading
 *   - Focus moves to first interactive element on open
 *   - Escape key: handled by parent (bell button key handler)
 *   - Outside click: handled by parent (useClickOutside)
 */

import React, { useEffect, useRef } from 'react';
import { NotificationRow } from './NotificationRow';
import { NotificationRowSkeleton } from './NotificationRowSkeleton';
import type { NotificationItem } from '../api/notifications';

interface Props {
  notifications: NotificationItem[];
  isLoading: boolean;
  onClose: () => void;
  onNotificationClick: (n: NotificationItem) => void;
}

export function NotificationsDropdown({
  notifications,
  isLoading,
  onClose,
  onNotificationClick,
}: Props) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dropdown on open.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  const showFooter = !isLoading && notifications.length >= 20;

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      aria-modal="true"
      aria-busy={isLoading ? 'true' : 'false'}
      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-hairline z-50 overflow-hidden flex flex-col"
      style={{ maxHeight: 440 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-hairline flex-shrink-0">
        <span className="text-sm font-semibold text-gray-900">Notifications</span>
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close notifications"
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Body — scrollable */}
      <div className="overflow-y-auto flex-1" role="list" aria-label="Notification items">
        {isLoading ? (
          <>
            <NotificationRowSkeleton />
            <NotificationRowSkeleton />
            <NotificationRowSkeleton />
          </>
        ) : notifications.length === 0 ? (
          <EmptyState />
        ) : (
          notifications.map((n) => (
            <NotificationRow key={n.id} notification={n} onClick={onNotificationClick} />
          ))
        )}
      </div>

      {/* Footer — only shown when API returned 20 items (may be more) */}
      {showFooter && (
        <div className="px-4 py-3 border-t border-hairline flex-shrink-0">
          <span
            className="text-sm text-gray-400 cursor-default"
            title="Full notification history coming soon"
          >
            View older notifications →
          </span>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="px-6 py-8 flex flex-col items-center text-center gap-3">
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-300"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <p className="text-sm text-gray-600 leading-relaxed">
        No notifications yet. We&rsquo;ll alert you when your saved schools&rsquo; scores change
        or new ID camps are added.
      </p>
    </div>
  );
}
